import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useContext } from 'react';

import { Ranking } from '@flipster/apis/v2/trading-competition/types';
import { useRouter } from '@flipster/i18n/routing';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import LockClosedIcon from '@flipster/react/icon/generated/LockClosedIcon';
import { css } from '@flipster/styled-system/css';
import { center, flex, grid, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { CompetitionsEvent } from '@flipster/utils/segment/events/competitions';
import webviewHandler from '@flipster/utils/webview-handler';

import { ImgOrLottie, contentfulImageUrl } from '../../../../contentful/assets';
import { rankingStyle } from '../../content/leaderboard/rank-list/ranking.style';
import { CompetitionIdContext, competitionBunja, competitionTabsBunja } from '../../state';

export const TableRow = ({ item, index }: { item: Ranking; index: number }) => {
  const id = useContext(CompetitionIdContext);
  const { competitionContentful } = useBunja(competitionBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const router = useRouter();

  const isRoi = selectedEventTabValue === 'roi';

  const getIconUrl = (rank?: number) => {
    if (!rank) return;

    const url =
      rank === 1
        ? competitionContentful?.rank1Icon?.url
        : rank === 2
          ? competitionContentful?.rank2Icon?.url
          : competitionContentful?.rank3Icon?.url;
    return contentfulImageUrl(url ?? '');
  };

  const isTop3 = item.rank && item.rank <= 3;

  return (
    <li
      key={item.rank}
      className={css(
        grid.raw({
          columns: 3,
          alignItems: 'center',
          w: '100%',
          h: 60,
          px: 24,
          rounded: 8,
          cursor: 'pointer',
          _hover: {
            bg: 'background.interaction.hover',
          },
        }),
        rankingStyle.raw({ rank: isTop3 ? (item.rank as 1 | 2 | 3) : 'default' }),
      )}
      onClick={() => {
        segment.track(CompetitionsEvent.CompetitionTradingLastLeaderboardListClick, {
          competition_id: id,
          nickname: item.nickname ?? '',
          list_order: index,
        });
        router.push(`/user/profile/${item.userProfileId}`);
      }}
    >
      <div className={hstack({ gap: 12, w: '100%', minW: 0 })}>
        {isTop3 ? (
          <ImgOrLottie url={getIconUrl(item.rank)} alt={`rank${item.rank}icon`} css={{ w: 40, h: 40 }} />
        ) : (
          <span
            className={center({
              w: 28,
              h: 28,
              color: 'content.default.level-2',
              textStyle: 'Text/M/Monospace/Strong',
            })}
          >
            {item.rank}
          </span>
        )}
        <button
          type='button'
          className={flex({
            align: 'center',
            gap: 2,
            color: 'content.default.level-5',
            textStyle: 'Text/L/Strong',
            flex: 1,
            minW: 0,
            overflow: 'hidden',
          })}
        >
          <span>{item.nickname ?? 'Nickname'}</span>
          {item.profileVisibility === 'PRIVATE' && <LockClosedIcon size={14} />}
          {item.profileVisibility === 'PUBLIC' && (
            <ChevronRightIcon
              size={14}
              color={isTop3 ? token('colors.inverse.white') : token('colors.content.default.level-1')}
            />
          )}
        </button>
      </div>
      <Formatted.Number
        value={item.prize ?? 0}
        css={{ textStyle: 'Text/L/Monospace/Regular', textAlign: 'right', color: 'content.default.level-5' }}
      />
      {isRoi ? (
        <Formatted.Percent
          value={item.value}
          isRate
          toFixed
          withPlus
          withColor
          css={{ textStyle: 'Text/L/Monospace/Regular', justifyContent: 'flex-end', color: 'content.default.level-5' }}
        />
      ) : (
        <Formatted.Number
          value={item.value ?? 0}
          toFixed
          precision={2}
          withPlus
          withColor
          css={{ textStyle: 'Text/L/Monospace/Regular', textAlign: 'right', color: 'content.default.level-5' }}
        />
      )}
    </li>
  );
};

export const MobileTableRow = ({ item, index }: { item: Ranking; index: number }) => {
  const id = useContext(CompetitionIdContext);
  const { competitionContentful } = useBunja(competitionBunja);
  const { selectedEventTabValueAtom } = useBunja(competitionTabsBunja);
  const selectedEventTabValue = useAtomValue(selectedEventTabValueAtom);
  const router = useRouter();

  const isRoi = selectedEventTabValue === 'roi';

  const getIconUrl = (rank?: number) => {
    if (!rank) return;

    const url =
      rank === 1
        ? competitionContentful?.rank1Icon?.url
        : rank === 2
          ? competitionContentful?.rank2Icon?.url
          : competitionContentful?.rank3Icon?.url;
    return contentfulImageUrl(url ?? '');
  };

  const isTop3 = item.rank && item.rank <= 3;

  return (
    <li
      key={item.rank}
      className={css(
        grid.raw({
          columns: 2,
          alignItems: 'center',
          w: '100%',
          h: 60,
          px: 16,
          rounded: 8,
          cursor: 'pointer',
          _hover: {
            bg: 'background.interaction.hover',
          },
        }),
        rankingStyle.raw({ rank: isTop3 ? (item.rank as 1 | 2 | 3) : 'default' }),
      )}
      onClick={() => {
        segment.track(CompetitionsEvent.CompetitionTradingLastLeaderboardListClick, {
          competition_id: id,
          nickname: item.nickname ?? '',
          list_order: index,
        });
        webviewHandler.postMessage(
          {
            action: 'link',
            params: {
              page: `user/profile/${item.userProfileId}`,
            },
          },
          () => router.push(`/user/profile/${item.userProfileId}`),
        );
      }}
    >
      <div className={hstack({ gap: 12, w: '100%' })}>
        {isTop3 ? (
          <ImgOrLottie url={getIconUrl(item.rank)} alt={`rank${item.rank}icon`} css={{ w: 40, h: 40 }} />
        ) : (
          <span
            className={center({
              w: 28,
              h: 28,
              color: 'content.default.level-2',
              textStyle: 'Text/M/Monospace/Strong',
            })}
          >
            {item.rank}
          </span>
        )}
        <button
          type='button'
          className={flex({
            align: 'center',
            gap: 2,
            color: 'content.default.level-5',
            textStyle: 'Text/L/Strong',
            flex: 1,
            overflow: 'hidden',
          })}
        >
          <span
            className={css({
              flex: 1,
              minW: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            })}
          >
            {item.nickname ?? 'Nickname'}
          </span>
          {item.profileVisibility === 'PRIVATE' && <LockClosedIcon size={14} />}
          {item.profileVisibility === 'PUBLIC' && (
            <ChevronRightIcon
              size={14}
              color={isTop3 ? token('colors.inverse.white') : token('colors.content.default.level-1')}
            />
          )}
        </button>
      </div>
      {isRoi ? (
        <Formatted.Percent
          value={item.value}
          isRate
          toFixed
          withPlus
          withColor
          css={{ textStyle: 'Text/L/Monospace/Regular', justifyContent: 'flex-end', color: 'content.default.level-5' }}
        />
      ) : (
        <Formatted.Number
          value={item.value ?? 0}
          toFixed
          precision={2}
          withPlus
          withColor
          css={{ textStyle: 'Text/L/Monospace/Regular', textAlign: 'right', color: 'content.default.level-5' }}
        />
      )}
    </li>
  );
};
